import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import VectorIcon from 'assets/actionPlan/vector.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import dayjs from 'dayjs';
import { notifySuccess } from 'utils/notifyConfig';

import {
  createAction,
  getActions,
  updateAction,
} from '../../../features/actions/actionPlanActions';
import { selectCreateLoading } from '../../../features/actions/actionPlanSlice';
import { actionSchema } from '../validationSchemas';
import {
  AlertIcon,
  ButtonWrapper,
  ErrorForInputs,
  ErrorMessage,
  FormWrapper,
  StyledTextAreaWrapper,
  SwitchWrapper,
} from './ActionEditModal.styles';

const ActionEditModal = ({
  isOpen,
  onClose,
  onSuccess,
  selectedAction,
  showEffectiveDate = true,
}) => {
  const dispatch = useDispatch();
  const isCreating = useSelector(selectCreateLoading);

  const [actionName, setActionName] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tomorrow = dayjs().add(1, 'day').startOf('day');
  const isEditMode = !!selectedAction?.id;
  const prevIsOpen = useRef(isOpen);

  // const shouldShowEffectiveDate = isEditMode
  //   ? Number(selectedAction?.activeAssignmentsCount || 0) > 0 && showEffectiveDate
  //   : showEffectiveDate;
  const hasAssignments = Number(selectedAction?.activeAssignmentsCount || 0) > 0;
  const shouldShowEffectiveDate = isEditMode && showEffectiveDate && hasAssignments;
  const [descError, setDescError] = useState('');

  const handleClose = () => {
    setActionName('');
    setDescription('');
    setDate(null);
    setIsEnabled(true);
    setErrors({});
    setDescError('');
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const trimmedName = actionName.trim();
      const trimmedDescription = description.trim();

      const validationData = {
        actionName: trimmedName,
        description: trimmedDescription,
      };

      if (shouldShowEffectiveDate) {
        validationData.effectiveDate = date ? dayjs(date).startOf('day').toDate() : null;
        await actionSchema.validate(validationData, { abortEarly: false });
      } else {
        await actionSchema.pick(['actionName', 'description']).validate(validationData, {
          abortEarly: false,
        });
      }

      const actionData = {
        actionName: trimmedName,
        description: trimmedDescription,
      };

      if (shouldShowEffectiveDate) {
        actionData.effectiveDate = dayjs(date).format('YYYY-MM-DD');
      } else if (!isEditMode) {
        actionData.effectiveDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
      }

      if (!isEditMode) {
        actionData.enabled = isEnabled;
      }

      let resultAction;

      if (isEditMode) {
        resultAction = await dispatch(
          updateAction({
            id: selectedAction.id,
            actionData,
          })
        );
      } else {
        resultAction = await dispatch(createAction(actionData));
      }

      const isSuccess =
        (isEditMode && updateAction.fulfilled.match(resultAction)) ||
        (!isEditMode && createAction.fulfilled.match(resultAction));

      if (isSuccess) {
        if (isEditMode) {
          const formattedDate = date
            ? dayjs(date).format('YYYY-MM-DD')
            : selectedAction?.effectiveDate
              ? dayjs(selectedAction.effectiveDate).format('YYYY-MM-DD')
              : '';

          if (formattedDate) {
            notifySuccess(
              `Update scheduled successfully. It will take effect on ${formattedDate}`
            );
          } else {
            notifySuccess('Action updated successfully.');
          }
        }
        if (onSuccess) onSuccess();
        else dispatch(getActions());
        setDescError('');
        handleClose();
      } else {
        setErrors({
          actionName: 'Action name already exists',
        });
      }
    } catch (err) {
      const validationErrors = {};
      if (err.inner) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
      }
      setErrors(validationErrors);
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    if (isOpen && selectedAction?.id) {
      console.log('REAL DATA:', selectedAction);
    }
    if (!isOpen) return;

    if (selectedAction?.id) {
      // const initialName = selectedAction.pendingActionName || selectedAction.actionName || '';
      const initialName = selectedAction.pendingActionName ?? selectedAction.actionName ?? '';

      const initialDesc =
        selectedAction.pendingDescription ?? selectedAction.description ?? '';

      setActionName(initialName);
      setDescription(initialDesc);
      setDate(selectedAction.effectiveDate ? dayjs(selectedAction.effectiveDate) : null);
    } else {
      setActionName('');
      setDescription('');
      setDate(null);
    }

    prevIsOpen.current = isOpen;
  }, [isOpen, selectedAction?.id]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit Action' : 'Create New Action'}
      width="448px"
      $centered
      footer={false}
    >
      <FormWrapper>
        <div>
          <Input
            label="Action Name"
            required
            placeholder="Enter action name"
            value={actionName}
            onChange={(e) => {
              setActionName(e.target.value);
              if (errors.actionName) setErrors((prev) => ({ ...prev, actionName: null }));
            }}
            error={!!errors.actionName}
            fullWidth
          />
          {errors.actionName && (
            <ErrorForInputs className="input-error">
              <AlertIcon src={VectorIcon} />
              <ErrorMessage>{errors.actionName}</ErrorMessage>
            </ErrorForInputs>
          )}
        </div>

        <StyledTextAreaWrapper>
          <TextArea
            key={selectedAction?.id || 'new'}
            label="Description"
            tooltip="Add a description"
            placeholder="Add description"
            value={description}
            onChange={(e) => {
              const rawValue = e.target.value;

              if (rawValue.length >= 250) {
                setDescError(
                  'Text exceeds the maximum limit of 250 characters. Only the first 250 characters will be kept.'
                );
              } else {
                setDescError('');
              }

              setDescription(rawValue.slice(0, 250));
            }}
            maxLength={250}
            resizeHorizontal={false}
          />
          {descError && (
            <ErrorForInputs>
              <AlertIcon src={VectorIcon} />
              <ErrorMessage className="textarea-message">{descError}</ErrorMessage>
            </ErrorForInputs>
          )}
        </StyledTextAreaWrapper>

        {shouldShowEffectiveDate && (
          <div>
            <CustomDatePicker
              label="Effective date"
              required
              placeholder="Select a date"
              value={date}
              minDate={tomorrow}
              onChange={(newValue) => {
                setDate(newValue);
                if (errors.effectiveDate)
                  setErrors((prev) => ({ ...prev, effectiveDate: null }));
              }}
              error={!!errors.effectiveDate}
              slotProps={{
                actionBar: {
                  actions: [],
                },
              }}
            />
            {errors.effectiveDate && (
              <ErrorForInputs>
                <AlertIcon src={VectorIcon} />
                <ErrorMessage>{errors.effectiveDate}</ErrorMessage>
              </ErrorForInputs>
            )}
          </div>
        )}

        {/* {!isEditMode && (
          <SwitchWrapper>
            <Switch isOn={isEnabled} onToggle={() => setIsEnabled(!isEnabled)} />
            {isEnabled ? 'Enabled' : 'Disabled'}
          </SwitchWrapper>
        )} */}

        <ButtonWrapper>
          <Button outlined onClick={handleClose} width="48%">
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit}
            width="48%"
            loading={isSubmitting || isCreating}
          >
            {isEditMode ? 'Save' : 'Create Action'}
          </Button>
        </ButtonWrapper>
      </FormWrapper>
    </Modal>
  );
};

export default ActionEditModal;
